import { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, CheckCircle2, RefreshCw, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import SignaturePad from './ui/SignaturePad';

const todayDE = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
};

// Nachträgliche RV-Befreiung (vor Ort) für einen bestehenden Personalbogen.
// Schreibt rv_antrag_an (+ optional rv_antrag_ag) über den bogen-basierten Endpoint.
export const RvNachholenDialog = ({ bogen, open, onClose, onDone, API, headers }) => {
  const [merkblatt, setMerkblatt] = useState(false);
  const [ort, setOrt] = useState('');
  const [saving, setSaving] = useState(false);
  const maRef = useRef(null);
  const agRef = useRef(null);
  const [maSig, setMaSig] = useState(null);
  const [agSig, setAgSig] = useState(null);

  if (!bogen) return null;

  const name = bogen.mitarbeiter_name
    || `${bogen.personal?.vorname || ''} ${bogen.personal?.familienname || ''}`.trim()
    || bogen.arbeitgeber?.berufsbezeichnung || 'Mitarbeiter';

  const captureMa = () => setMaSig(maRef.current && !maRef.current.isEmpty() ? maRef.current.toDataURL('image/png') : null);
  const captureAg = () => setAgSig(agRef.current && !agRef.current.isEmpty() ? agRef.current.toDataURL('image/png') : null);

  const handleSave = async () => {
    if (!merkblatt) { toast.error('Bitte die Merkblatt-Bestätigung ankreuzen'); return; }
    const maFinal = maSig || (maRef.current && !maRef.current.isEmpty() ? maRef.current.toDataURL('image/png') : null);
    if (!maFinal) { toast.error('Mitarbeiter-Unterschrift fehlt'); return; }
    const agFinal = agSig || (agRef.current && !agRef.current.isEmpty() ? agRef.current.toDataURL('image/png') : null);
    setSaving(true);
    try {
      const datum = todayDE();
      await axios.put(`${API}/personalbogen/${bogen.id}/admin-rv-complete`, {
        an_merkblatt_gelesen: true,
        an_ort: ort || null,
        an_datum: datum,
        an_signature: maFinal,
        ort_ag: ort || null,
        datum_unterschrift_ag: agFinal ? datum : null,
        signature_ag: agFinal || null,
      }, { headers });
      toast.success(agFinal ? 'RV-Befreiung vollständig erfasst ✅' : 'Mitarbeiter-Unterschrift gespeichert ✅');
      onDone?.();
      onClose?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Fehler beim Speichern'));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="rv-nachholen-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-violet-600" /> RV-Befreiung nachholen
          </DialogTitle>
          <DialogDescription>
            Befreiungsantrag von der Rentenversicherungspflicht (§ 6 Abs. 1b SGB VI) für <span className="font-medium text-foreground">{name}</span> vor Ort unterschreiben lassen.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {/* Merkblatt */}
          <div
            className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${merkblatt ? 'border-orange-400 bg-orange-50 dark:bg-orange-900/20' : 'border-border'}`}
            onClick={() => setMerkblatt(v => !v)}
            data-testid="rv-nachholen-merkblatt"
          >
            <div className={`h-5 w-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 ${merkblatt ? 'border-orange-500 bg-orange-500' : 'border-muted-foreground'}`}>
              {merkblatt && <CheckCircle2 className="h-3.5 w-3.5 text-white" />}
            </div>
            <p className="text-sm">Der Mitarbeiter hat das Merkblatt zur Befreiung gelesen und beantragt die Befreiung von der Rentenversicherungspflicht.</p>
          </div>

          <div>
            <Label className="text-xs">Ort (für beide Unterschriften)</Label>
            <Input value={ort} onChange={e => setOrt(e.target.value)} placeholder="z.B. Fellbach" data-testid="rv-nachholen-ort" />
          </div>

          {/* MA Unterschrift */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs">Unterschrift Mitarbeiter <span className="text-red-500">*</span></Label>
              <button type="button" onClick={() => { maRef.current?.clear(); setMaSig(null); }}
                className="text-xs text-muted-foreground flex items-center gap-1 hover:text-foreground">
                <RefreshCw className="h-3 w-3" /> Zurücksetzen
              </button>
            </div>
            <div className="rounded-lg border bg-white">
              <SignaturePad ref={maRef} penColor="#1E40AF" height={140} onEnd={captureMa} />
            </div>
          </div>

          {/* AG Unterschrift (optional) */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs">Unterschrift Arbeitgeber <span className="text-muted-foreground">(optional)</span></Label>
              <button type="button" onClick={() => { agRef.current?.clear(); setAgSig(null); }}
                className="text-xs text-muted-foreground flex items-center gap-1 hover:text-foreground">
                <RefreshCw className="h-3 w-3" /> Zurücksetzen
              </button>
            </div>
            <div className="rounded-lg border bg-white">
              <SignaturePad ref={agRef} penColor="#EA580C" height={140} onEnd={captureAg} />
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Kann auch später über die Mitarbeiter-Detailseite nachgetragen werden.</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Abbrechen</Button>
          <Button onClick={handleSave} disabled={saving || !merkblatt} data-testid="rv-nachholen-save"
            className="bg-violet-600 hover:bg-violet-700">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RvNachholenDialog;
